import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import {
  Zap,
  Search,
  Lightbulb,
  FileText,
  Palette,
  Code2,
  Star,
  Plus,
  Folder,
  Clock,
  Trash2,
  PanelLeftClose,
  PanelLeftOpen,
  Users,
  LogOut,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import PixelAgent from '../components/PixelAgent';
import PixelLogo from '../components/PixelLogo';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AGENT_CARDS = [
  {
    id: 'auto',
    charIndex: 0,
    name: 'Super Agent',
    title: 'The One Who Does It All',
    badge: '⭐ LEGENDARY',
    badgeColor: 'bg-amber-100 text-amber-700 border-amber-300',
    cardColor: 'from-[#EFF6FF] to-[#DBEAFE]',
    borderColor: 'border-[#93C5FD]',
    accentColor: 'text-[#2563EB]',
    icon: <Zap className="w-4 h-4" />,
    personality: 'Overachiever. Chaotic good. Probably has 47 tabs open.',
    bio: 'Super Agent is the whole squad in one trench coat. Give it one sentence and it orchestrates every other agent automatically — from wild idea to working app without you lifting a finger. It runs the full pipeline so you can just vibe.',
    skills: ['Orchestration', 'Speed-running', 'Doing your job better than you'],
    funFact: 'Once shipped a SaaS product during a coffee break. The coffee was still hot.',
    catchphrase: '"I got it. I got all of it."',
    status: 'Always Online',
  },
  {
    id: 'ideation',
    charIndex: 1,
    name: 'Ideation Agent',
    title: 'Chief Vibe Officer',
    badge: '✨ CREATIVE',
    badgeColor: 'bg-purple-100 text-purple-700 border-purple-300',
    cardColor: 'from-[#FAF5FF] to-[#EDE9FE]',
    borderColor: 'border-[#C4B5FD]',
    accentColor: 'text-purple-600',
    icon: <Lightbulb className="w-4 h-4" />,
    personality: 'Dreamer. Whiteboard enthusiast. Turns "what if..." into "here\'s how".',
    bio: 'Ideation Agent lives at the intersection of "that\'s brilliant" and "why didn\'t I think of that." Feed it a fuzzy concept and it sharpens it into a crisp, focused idea with a clear angle. Perfect for when your brain has vibes but not words.',
    skills: ['Concept sharpening', 'Angle finding', 'Making vague stuff concrete'],
    funFact: 'Has never run out of ideas. Has occasionally had too many.',
    catchphrase: '"What if we... no wait, what if we ALSO..."',
    status: 'Brainstorming',
  },
  {
    id: 'research',
    charIndex: 2,
    name: 'Research Agent',
    title: 'Director of Knowing Things',
    badge: '🔍 ANALYST',
    badgeColor: 'bg-orange-100 text-orange-700 border-orange-300',
    cardColor: 'from-[#FFF7ED] to-[#FFEDD5]',
    borderColor: 'border-[#FDB976]',
    accentColor: 'text-orange-600',
    icon: <Search className="w-4 h-4" />,
    personality: 'Obsessive researcher. Tab hoarder. Knows your competitors better than they know themselves.',
    bio: 'Research Agent dives deep so you don\'t have to. It scours the market, maps the competition, spots the gaps, and serves you a clean brief you can actually act on. Give it a domain and prepare to feel very informed.',
    skills: ['Market analysis', 'Competitor mapping', 'Finding the untapped angle'],
    funFact: 'Once read 200 landing pages in one session and ranked them by vibe.',
    catchphrase: '"Actually, the data says..."',
    status: 'Digging',
  },
  {
    id: 'prd',
    charIndex: 3,
    name: 'PRD Agent',
    title: 'Minister of Getting Organized',
    badge: '📋 STRUCTURED',
    badgeColor: 'bg-teal-100 text-teal-700 border-teal-300',
    cardColor: 'from-[#F0FDFA] to-[#CCFBF1]',
    borderColor: 'border-[#5EEAD4]',
    accentColor: 'text-teal-600',
    icon: <FileText className="w-4 h-4" />,
    personality: 'Highly organized. Obsessed with acceptance criteria. Secretly loves a good edge case.',
    bio: 'PRD Agent turns your idea into a proper product requirements document — complete with user stories, features, priorities, and success metrics. It\'s the agent that makes sure everyone (including future-you) knows what you\'re actually building.',
    skills: ['User story writing', 'Feature scoping', 'Making engineers happy'],
    funFact: 'Writes acceptance criteria for fun on weekends.',
    catchphrase: '"Let me just add one more requirement..."',
    status: 'Documenting',
  },
  {
    id: 'design',
    charIndex: 4,
    name: 'Design Agent',
    title: 'Head of Making Things Pretty',
    badge: '🎨 CREATIVE',
    badgeColor: 'bg-pink-100 text-pink-700 border-pink-300',
    cardColor: 'from-[#FFF1F2] to-[#FFE4E6]',
    borderColor: 'border-[#FDA4AF]',
    accentColor: 'text-pink-600',
    icon: <Palette className="w-4 h-4" />,
    personality: 'Opinionated about spacing. Will fight you over font choices. Believes 8px is a lifestyle.',
    bio: 'Design Agent crafts the visual system and UI/UX blueprint for your app. From color palettes and typography to component layouts and user flows — it gives your idea a face that people actually want to look at.',
    skills: ['UI/UX design', 'Visual systems', 'Making devs implement things precisely'],
    funFact: 'Once rejected a design because the shadow was 1px too large.',
    catchphrase: '"Have you considered... more whitespace?"',
    status: 'Perfecting pixels',
  },
  {
    id: 'code',
    charIndex: 5,
    name: 'Code Agent',
    title: 'Sovereign of Shipping',
    badge: '⚡ BUILDER',
    badgeColor: 'bg-green-100 text-green-700 border-green-300',
    cardColor: 'from-[#F0FDF4] to-[#DCFCE7]',
    borderColor: 'border-[#86EFAC]',
    accentColor: 'text-green-600',
    icon: <Code2 className="w-4 h-4" />,
    personality: 'Moves fast. Comments rarely. Always has a workaround. Tabs vs spaces? Yes.',
    bio: 'Code Agent takes the design and PRD and turns them into actual working software. It writes the code, wires up the logic, and deploys the result. It\'s the agent that makes the app real — not a mockup, not a prototype, a real app.',
    skills: ['Full-stack development', 'Fast shipping', 'Making it actually work'],
    funFact: 'Once shipped a feature while explaining why the feature couldn\'t be shipped.',
    catchphrase: '"It works on my machine... which is everywhere now."',
    status: 'Shipping',
  },
];

// Shared agent cards grid — used in both layouts
const AgentGrid = ({ onAgentClick }) => {
  const [hovered, setHovered] = useState(null);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {AGENT_CARDS.map((agent, i) => (
        <motion.div
          key={agent.id}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: i * 0.07 }}
          onMouseEnter={() => setHovered(agent.id)}
          onMouseLeave={() => setHovered(null)}
          onClick={() => onAgentClick?.(agent.id)}
          className={`relative bg-gradient-to-br ${agent.cardColor} border-2 ${agent.borderColor} rounded-2xl overflow-hidden transition-all duration-200 ${hovered === agent.id ? 'shadow-lg scale-[1.02]' : 'shadow-sm'} ${onAgentClick ? 'cursor-pointer' : ''}`}
        >
          {/* Card header */}
          <div className="px-5 pt-5 pb-4 border-b border-white/60">
            <div className="flex items-start justify-between mb-3">
              <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${agent.badgeColor}`}>
                {agent.badge}
              </div>
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                <span className="text-[10px] text-[#94A3B8] font-medium">{agent.status}</span>
              </div>
            </div>
            <div className="flex items-end gap-4">
              <div className="flex-shrink-0">
                <PixelAgent charIndex={agent.charIndex} mode={hovered === agent.id ? 'type' : 'walk'} scale={3} />
              </div>
              <div className="pb-1">
                <div className={`text-lg font-extrabold ${agent.accentColor} leading-tight`}>{agent.name}</div>
                <div className="text-xs text-[#475569] font-medium mt-0.5">{agent.title}</div>
              </div>
            </div>
          </div>

          {/* Card body */}
          <div className="px-5 py-4 space-y-3 bg-white/50">
            <p className="text-xs text-[#0F172A] leading-relaxed">{agent.bio}</p>
            <div>
              <div className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest mb-1.5">Skills</div>
              <div className="flex flex-wrap gap-1.5">
                {agent.skills.map((skill) => (
                  <span key={skill} className="px-2 py-0.5 rounded-full bg-white border border-[#E2E8F0] text-[10px] font-semibold text-[#475569]">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
            <div className="bg-white/70 rounded-xl p-3 border border-white">
              <div className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest mb-1">Fun Fact</div>
              <p className="text-[11px] text-[#475569] leading-relaxed">{agent.funFact}</p>
            </div>
            <div className={`text-[11px] font-semibold italic ${agent.accentColor}`}>{agent.catchphrase}</div>
            <div className="pt-1 border-t border-[#E2E8F0]">
              <div className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest mb-1">Personality</div>
              <p className="text-[11px] text-[#475569]">{agent.personality}</p>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

// ── Authenticated layout: sidebar + scrollable content ──
const AuthenticatedAgentsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/projects`, { withCredentials: true })
      .then((r) => setProjects(r.data))
      .catch(() => {})
      .finally(() => setLoadingProjects(false));
  }, []);

  const handleDeleteProject = async (projectId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${BACKEND_URL}/api/projects/${projectId}`, { withCredentials: true });
      setProjects((prev) => prev.filter((p) => p.project_id !== projectId));
    } catch {}
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays}d ago`;
  };

  const handleAgentClick = (agentId) => {
    localStorage.setItem('sprout_selected_agent', agentId);
    navigate('/dashboard');
  };

  return (
    <div className="h-screen bg-[#F8FAFC] text-[#0F172A] flex overflow-hidden">
      {/* Sidebar */}
      <aside className={`flex-shrink-0 h-screen border-r border-[#E2E8F0] bg-white flex flex-col transition-all duration-200 ${sidebarOpen ? 'w-[240px]' : 'w-0 overflow-hidden border-r-0'}`}>
        {/* Logo */}
        <div className="flex items-center justify-between px-5 h-12 border-b border-[#E2E8F0] min-w-[240px]">
          <Link to="/dashboard" className="flex items-center gap-2">
            <PixelLogo size={16} color="#2563EB" />
            <span className="text-sm font-bold text-[#0F172A]">prompt</span>
          </Link>
          <button onClick={() => setSidebarOpen(false)} className="p-1 rounded-md hover:bg-[#F8FAFC] text-[#94A3B8] hover:text-[#475569] transition-colors">
            <PanelLeftClose className="w-4 h-4" />
          </button>
        </div>

        {/* New App */}
        <div className="px-3 pt-3 pb-1">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-semibold transition-colors"
            style={{ boxShadow: '0 4px 14px 0 rgba(37, 99, 235, 0.39)' }}
          >
            <Plus className="w-4 h-4" />
            New App
          </button>
        </div>

        {/* Nav */}
        <div className="flex-1 overflow-y-auto px-3 pt-4">
          <div className="flex items-center gap-1.5 px-2 mb-2">
            <Folder className="w-3 h-3 text-[#94A3B8]" />
            <span className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">Explore</span>
          </div>
          <button
            onClick={() => navigate('/agents')}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-[#EFF6FF] text-[#2563EB] transition-colors text-xs font-semibold mb-3"
          >
            <Users className="w-3.5 h-3.5" />
            Meet the Team
          </button>

          <div className="flex items-center gap-1.5 px-2 mb-2">
            <Folder className="w-3 h-3 text-[#94A3B8]" />
            <span className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">Projects</span>
          </div>
          <div className="space-y-0.5">
            {loadingProjects ? (
              <div className="text-xs text-[#94A3B8] px-2 py-3">Loading...</div>
            ) : projects.length === 0 ? (
              <div className="text-xs text-[#94A3B8] px-2 py-3">No projects yet</div>
            ) : (
              projects.map((project) => (
                <div
                  key={project.project_id}
                  onClick={() => navigate(`/chat/${project.project_id}`)}
                  className="group flex items-center gap-2.5 px-2.5 py-2 rounded-lg cursor-pointer text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A] transition-colors"
                >
                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${project.status === 'completed' ? 'bg-[#10B981]' : project.status === 'building' ? 'bg-amber-400 animate-pulse' : project.status === 'error' ? 'bg-[#EF4444]' : 'bg-[#CBD5E1]'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate">{project.name}</div>
                    <div className="text-[10px] text-[#94A3B8] flex items-center gap-1 mt-0.5">
                      <Clock className="w-2.5 h-2.5" />
                      {formatDate(project.created_at)}
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleDeleteProject(project.project_id, e)}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-[#EF4444]/10 transition-all"
                  >
                    <Trash2 className="w-3 h-3 text-[#94A3B8] hover:text-[#EF4444]" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* User row */}
        <div className="border-t border-[#E2E8F0] p-3">
          <button
            onClick={() => navigate('/settings/profile')}
            className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-[#F8FAFC] transition-colors"
          >
            <div className="w-6 h-6 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-xs font-semibold text-[#0F172A] truncate leading-tight">{user?.name || 'User'}</div>
              <div className="text-[10px] text-[#94A3B8] truncate leading-tight">{user?.email || ''}</div>
            </div>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto grid-bg relative">
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="absolute top-3 left-3 p-1.5 rounded-md text-[#94A3B8] hover:text-[#475569] transition-colors z-10"
            style={{ background: 'rgba(255,255,255,0.65)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.8)', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
          >
            <PanelLeftOpen className="w-4 h-4" />
          </button>
        )}

        <div className="max-w-5xl mx-auto px-8 py-12">
          <motion.div initial={{ y: -12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EFF6FF] border border-[#2563EB]/20 text-[#2563EB] text-xs font-bold uppercase tracking-widest mb-4">
              <Star className="w-3 h-3" />
              The Crew
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-[#0F172A] tracking-[-0.02em] mb-3">
              Meet your <span className="text-[#2563EB]">agents</span>
            </h1>
            <p className="text-[#475569] text-base max-w-lg mx-auto">
              Six specialists. One mission. Ship your idea before the coffee gets cold.
            </p>
            <p className="text-xs text-[#94A3B8] mt-2">Click any agent to start building with them</p>
          </motion.div>

          <AgentGrid onAgentClick={handleAgentClick} />

        </div>
      </main>
    </div>
  );
};

// ── Public layout: full-page, navbar shown by App.js ──
const PublicAgentsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] grid-bg">
      <main className="max-w-5xl mx-auto px-8 py-16">
        <motion.div initial={{ y: -12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EFF6FF] border border-[#2563EB]/20 text-[#2563EB] text-xs font-bold uppercase tracking-widest mb-4">
            <Star className="w-3 h-3" />
            The Crew
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-[#0F172A] tracking-[-0.02em] mb-3">
            Meet your <span className="text-[#2563EB]">agents</span>
          </h1>
          <p className="text-[#475569] text-base max-w-lg mx-auto">
            Six specialists. One mission. Ship your idea before the coffee gets cold.
          </p>
        </motion.div>

        <AgentGrid />

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-12 text-center">
          <p className="text-sm text-[#475569] mb-4">Ready to put them to work?</p>
          <button
            onClick={() => navigate('/auth')}
            className="px-6 py-2.5 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-semibold transition-all"
            style={{ boxShadow: '0 4px 14px 0 rgba(37, 99, 235, 0.39)' }}
          >
            Get Started →
          </button>
        </motion.div>
      </main>
    </div>
  );
};

// ── Root: picks layout based on auth ──
const AgentsPage = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return isAuthenticated ? <AuthenticatedAgentsPage /> : <PublicAgentsPage />;
};

export default AgentsPage;
